import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/presentation/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/presentation/components/ui/alert-dialog';
import { Button } from '@/presentation/components/ui/button';
import type { GenerationStatus } from '@/types';

interface GenerationProgressModalProps {
  status: GenerationStatus;
  onCancel?: () => void;
}

export function GenerationProgressModal({ status, onCancel }: GenerationProgressModalProps) {
  const isOpen = status.isGenerating;

  return (
    <Dialog open={isOpen}>
      <DialogContent
        className="sm:max-w-md"
        showCloseButton={false}
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-center font-display text-warm-800">
            {status.currentStep === 'audio' ? '音声生成中' : '動画生成中'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {/* シーン進捗（大きく表示）*/}
          {status.totalScenes !== undefined && status.totalScenes > 0 && (
            <p className="text-center text-lg font-bold text-warm-700">
              {status.currentSceneIndex === 0
                ? '準備中...'
                : status.currentSceneIndex > status.totalScenes
                  ? '仕上げ中...'
                  : `シーン ${status.currentSceneIndex} / ${status.totalScenes} 処理中`}
            </p>
          )}

          {/* Progress Ring & Percentage */}
          <div className="flex items-center justify-center">
            <div className="relative">
              <svg className="w-24 h-24 -rotate-90">
                <circle
                  cx="48"
                  cy="48"
                  r="40"
                  stroke="currentColor"
                  strokeWidth="8"
                  fill="none"
                  className="text-warm-200"
                />
                <circle
                  cx="48"
                  cy="48"
                  r="40"
                  stroke="currentColor"
                  strokeWidth="8"
                  fill="none"
                  strokeDasharray={`${2 * Math.PI * 40}`}
                  strokeDashoffset={`${2 * Math.PI * 40 * (1 - status.progress / 100)}`}
                  strokeLinecap="round"
                  className={`transition-all duration-500 ease-out ${
                    status.currentStep === 'audio' ? 'text-coral-500' : 'text-teal-500'
                  }`}
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-2xl font-bold text-warm-800">{status.progress}%</span>
              </div>
            </div>
          </div>

          {/* 詳細メッセージ */}
          <div className="text-center space-y-2">
            <div className="flex items-center justify-center gap-2 text-sm text-warm-600">
              <Loader2 size={14} className="animate-spin text-coral-500" />
              {status.message}
            </div>
          </div>

          {/* プログレスバー */}
          <div className="progress-bar-track">
            <div
              className="progress-bar-fill"
              style={{ width: `${Math.min(status.progress, 100)}%` }}
            />
          </div>

          <p className="text-xs text-center text-warm-400">
            生成中はこのタブを閉じないでください
          </p>

          {/* Cancel Button with Confirmation Dialog */}
          {onCancel && (
            <div className="flex justify-center pt-2">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm" className="text-warm-500 hover:text-warm-700">
                    キャンセル
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>生成を中止しますか？</AlertDialogTitle>
                    <AlertDialogDescription>
                      進行中の処理が中断されます。生成途中のデータは破棄されます。
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>続行</AlertDialogCancel>
                    <AlertDialogAction onClick={onCancel} className="bg-red-500 hover:bg-red-600">
                      中止
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
